import React, { useState, useEffect } from 'react'
import { apiConfig } from '@structura/services'
import { useTranslation } from '@structura/i18n'

interface CooperativeUser {
  id: number
  cooperativeUserId: string
  cooperativeUserEmail: string
  cooperativeUserAuth0Id?: string
  cooperativeUserName?: string
  cooperativeUserRole: string
  createdAt: string
}

export interface CooperativeUsersManagementProps {
  authToken: () => Promise<string>
  // Display label for the current app (e.g. "Wood", "Steel", "Concrete"). When
  // set, an inline note makes the per-app cooperator scoping visible. See
  // docs/scratch/cooperative-users-design.md.
  appLabel?: string
}

const CooperativeUsersManagement: React.FC<CooperativeUsersManagementProps> = ({ authToken, appLabel }) => {
  const { t } = useTranslation()
  const [cooperativeUsers, setCooperativeUsers] = useState<CooperativeUser[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)

  const [formUserId, setFormUserId] = useState<string>('')
  const [formEmail, setFormEmail] = useState<string>('')
  const [formAuth0Id, setFormAuth0Id] = useState<string>('')
  const [formName, setFormName] = useState<string>('')
  const [formRole, setFormRole] = useState<string>('Designer')

  const loadCooperativeUsers = async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const token: string = await authToken()
      const response: Response = await fetch(`${apiConfig.baseUrl}/api/CooperativeUsers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to load cooperative users (${response.status})`)
      }

      const data = await response.json()
      setCooperativeUsers(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.coop.errLoad'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCooperativeUsers()
  }, [])

  const resetForm = (): void => {
    setFormUserId('')
    setFormEmail('')
    setFormAuth0Id('')
    setFormName('')
    setFormRole('Designer')
    setShowAddForm(false)
  }

  const handleAdd = async (): Promise<void> => {
    if (!formUserId || !formEmail) {
      setError(t('auth.coop.errUserIdEmailRequired'))
      return
    }

    setSaving(true)
    setError(null)
    try {
      const token: string = await authToken()
      const response: Response = await fetch(`${apiConfig.baseUrl}/api/CooperativeUsers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cooperativeUserId: formUserId,
          cooperativeUserEmail: formEmail,
          cooperativeUserAuth0Id: formAuth0Id || undefined,
          cooperativeUserName: formName || undefined,
          cooperativeUserRole: formRole
        })
      })

      if (!response.ok) {
        const text: string = await response.text()
        throw new Error(text || `Failed to add cooperative user (${response.status})`)
      }

      await loadCooperativeUsers()
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.coop.errAdd'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number): Promise<void> => {
    if (!confirm(t('auth.coop.confirmRemove'))) return

    try {
      const token: string = await authToken()
      const response: Response = await fetch(`${apiConfig.baseUrl}/api/CooperativeUsers/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to delete cooperative user (${response.status})`)
      }

      await loadCooperativeUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.coop.errDelete'))
    }
  }

  return (
    <div className="account-card mb-4">
      <div className="card-header bg-transparent border-0 pt-4 ps-4 pe-4 d-flex justify-content-between align-items-center">
        <h5 className="fw-bold mb-0 text-dark-green">
          <i className="bi bi-people me-2"></i>{t('auth.coop.title')}
        </h5>
        {!showAddForm && (
          <button className="btn btn-outline-success btn-sm" onClick={() => setShowAddForm(true)}>
            <i className="bi bi-person-plus me-1"></i> {t('auth.coop.addUser')}
          </button>
        )}
      </div>
      <div className="card-body p-4">
        {appLabel && (
          <div className="alert alert-info py-2 mb-3 small">
            <i className="bi bi-info-circle me-2"></i>
            {t('auth.coop.perAppBefore')} <strong>{appLabel}</strong>{t('auth.coop.perAppAfter')}
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible py-2 mb-3">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        {showAddForm && (
          <div className="border rounded p-3 mb-3 bg-light">
            <h6 className="fw-bold mb-3">{t('auth.coop.addCoopUser')}</h6>
            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label small">{t('auth.coop.labelUserId')} <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formUserId}
                  onChange={e => setFormUserId(e.target.value)}
                  placeholder="a1b2c3d4-e5f6-7890-abcd-ef1234567890"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small">{t('auth.coop.labelEmail')} <span className="text-danger">*</span></label>
                <input
                  type="email"
                  className="form-control form-control-sm"
                  value={formEmail}
                  onChange={e => setFormEmail(e.target.value)}
                  placeholder="user@example.com"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small">{t('auth.coop.labelAuth0')}</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formAuth0Id}
                  onChange={e => setFormAuth0Id(e.target.value)}
                  placeholder="auth0|..."
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small">{t('auth.coop.labelName')}</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small">{t('auth.coop.labelRole')}</label>
                <select className="form-select form-select-sm" value={formRole} onChange={e => setFormRole(e.target.value)}>
                  <option value="Designer">{t('auth.coop.roleDesigner')}</option>
                  <option value="Approval">{t('auth.coop.roleApproval')}</option>
                  <option value="Info">{t('auth.coop.roleInfo')}</option>
                  <option value="All">{t('auth.coop.roleAll')}</option>
                </select>
              </div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-success btn-sm" onClick={handleAdd} disabled={saving}>
                {saving ? t('auth.coop.adding') : t('auth.coop.add')}
              </button>
              <button className="btn btn-light btn-sm" onClick={resetForm}>{t('auth.common.cancel')}</button>
            </div>
          </div>
        )}

        {loading && <div className="text-center py-3"><span className="spinner-border spinner-border-sm"></span></div>}

        {!loading && cooperativeUsers.length === 0 && !showAddForm && (
          <div className="text-center text-muted py-4">
            <i className="bi bi-people fs-1 d-block mb-2"></i>
            <p className="mb-0">{t('auth.coop.noUsersYet')}</p>
          </div>
        )}

        {!loading && cooperativeUsers.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t('auth.coop.thName')}</th>
                  <th>{t('auth.coop.thEmail')}</th>
                  <th>{t('auth.coop.thUserId')}</th>
                  <th>{t('auth.coop.thAuth0')}</th>
                  <th>{t('auth.coop.thRole')}</th>
                  <th className="text-end">{t('auth.coop.thActions')}</th>
                </tr>
              </thead>
              <tbody>
                {cooperativeUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.cooperativeUserName || '—'}</td>
                    <td><small className="text-muted">{user.cooperativeUserEmail}</small></td>
                    <td><code className="small">{user.cooperativeUserId.slice(0, 8)}...</code></td>
                    <td><small className="text-muted">{user.cooperativeUserAuth0Id || '—'}</small></td>
                    <td><span className="badge bg-secondary-subtle text-secondary">{user.cooperativeUserRole}</span></td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(user.id)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default CooperativeUsersManagement
